import {
  submitJob as submitJobRequest,
  createFileUploadUrl as createFileUploadUrlRequest,
  getJob as getJobRequest,
  terminateJob as terminateJobRequest,
  listMovementLogs as listMovementLogsRequest,
} from '@wix/bex-utils/@wix/ambassador-hub-v1-job/http';
import {
  FileFormat,
  type Destination,
  type DataMovementJob,
  type ListMovementLogsResponse,
  type Transformation,
  type CursorPaging,
} from '@wix/bex-utils/@wix/ambassador-hub-v1-job/types';
import type { PatternsErrorHandler } from '../../model';
import type { HttpClient } from '../../model/httpClient';

export interface HubJobClientDeps {
  httpClient: HttpClient;
  errorHandler: PatternsErrorHandler;
}

export interface SubmitImportJobParams {
  /** File id returned by `uploadFile`. */
  fileId: string;
  /**
   * Where the job writes. A union (`wixDataCollection`, `storesCatalogProducts`,
   * …) so platformized services pass their own variant — the client stays
   * neutral to that choice (see plan O6).
   */
  destination: Destination;
  /** Job name (the original file name). */
  name?: string;
  /** Source file format. Defaults to CSV. */
  fileFormat?: FileFormat;
  transformations?: Transformation[];
}

/**
 * Thin wrapper over the Hub Job (data-movement) API used by the server-side
 * import. Each call goes through `errorHandler.withErrorHandler` and the
 * container's `httpClient`, mirroring `ExportState`.
 *
 * Holds no observable state — it's a standalone unit consumed by `ImportState`.
 */
export interface HubJobClient {
  /**
   * Reserves an upload URL, PUTs the file, and returns its `fileId`.
   * Cancellation is handled at the job level via `terminateJob`, not by
   * aborting this upload.
   */
  uploadFile(file: File): Promise<string>;
  /** Submits an import job from an uploaded file; returns the `jobId`. */
  submitImportJob(params: SubmitImportJobParams): Promise<string>;
  /** Fetches a job's current state (status + progress) for polling. */
  getJob(jobId: string): Promise<DataMovementJob>;
  /** Requests termination of a running job. Does not roll back written rows. */
  terminateJob(jobId: string): Promise<void>;
  /** Lists per-row movement logs (source of the failed-rows report). */
  listMovementLogs(
    jobId: string,
    paging?: CursorPaging,
  ): Promise<ListMovementLogsResponse>;
}

export function createHubJobClient({
  httpClient,
  errorHandler,
}: HubJobClientDeps): HubJobClient {
  // Empty error-code map = default handling. Specific codes/messages need
  // translation keys and are deferred to the translations task.
  return {
    async uploadFile(file) {
      const {
        data: { uploadUrl, fileId },
      } = await errorHandler.withErrorHandler(
        () =>
          httpClient.request(
            createFileUploadUrlRequest({ fileName: file.name }),
          ),
        {},
      );

      if (!uploadUrl || !fileId) {
        throw new Error('createFileUploadUrl returned no uploadUrl/fileId');
      }

      await errorHandler.withErrorHandler(
        () => httpClient.request({ url: uploadUrl, method: 'PUT', data: file }),
        {},
      );

      return fileId;
    },

    async submitImportJob({
      fileId,
      destination,
      name,
      fileFormat = FileFormat.CSV,
      transformations,
    }) {
      const {
        data: { job },
      } = await errorHandler.withErrorHandler(
        () =>
          httpClient.request(
            submitJobRequest({
              name,
              source: { file: { fileId, format: fileFormat } },
              destination,
              ...(transformations && { transformations }),
            }),
          ),
        {},
      );

      if (!job?.id) {
        throw new Error('submitJob returned no job id');
      }

      return job.id;
    },

    async getJob(jobId) {
      const {
        data: { job },
      } = await errorHandler.withErrorHandler(
        () => httpClient.request(getJobRequest({ jobId })),
        {},
      );

      if (!job) {
        throw new Error(`getJob returned no job for "${jobId}"`);
      }

      return job;
    },

    async terminateJob(jobId) {
      await errorHandler.withErrorHandler(
        () => httpClient.request(terminateJobRequest({ jobId })),
        {},
      );
    },

    async listMovementLogs(jobId, paging) {
      const { data } = await errorHandler.withErrorHandler(
        () =>
          httpClient.request(
            listMovementLogsRequest({ jobId, ...(paging && { paging }) }),
          ),
        {},
      );

      return data;
    },
  };
}
